/**
 * Autovona product page behaviour (ProductDetail, ProductGallery, ImageZoom,
 * BundleSelector, SaleCountdown, StickyAddToCart, SizeChart, ProductTabs,
 * FrequentlyBoughtTogether). Depends on window.Autovona from autovona.js.
 */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const setState = (el, on) => window.Autovona?.setState(el, on);
  const cart = () => window.Autovona?.cart;

  /* ------------------------------------------------------------------ */
  /* Money — Shopify money_format ({{amount}}, {{amount_no_decimals}}, …) */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents, format = '${{amount}}') {
    const value = (Number(cents) || 0) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = n.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const out = format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return fmt(value, 0);
        case 'amount_with_comma_separator':
          return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return fmt(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return fmt(value, 0, ' ', ',');
        default:
          return fmt(value, 2);
      }
    });
    return out.replace(/<[^>]*>/g, '');
  }

  /** Replace the first text node of an element (keeps React-style <!-- --> splits intact). */
  function setFirstText(el, text) {
    if (!el) return;
    const node = [...el.childNodes].find((n) => n.nodeType === 3 && n.nodeValue.trim() !== '');
    if (node) node.nodeValue = text;
    else el.prepend(document.createTextNode(text));
  }

  /**
   * Update a price shown inside `el`.
   *
   * The Vitals currency converter wraps every price it finds in
   * <span class="money" data-vitals-cc-aud="$59.99">$59.99</span>. Writing a new
   * text node next to that span is what made prices appear twice ("$59.99$59.99"),
   * so when the wrapper exists we update *it* — its text and the original amount
   * it keeps — and drop any stale converted copies so the app re-converts.
   */
  function setMoney(el, text) {
    if (!el) return;
    const wrapped = $('.money', el);
    if (!wrapped) {
      setFirstText(el, text);
      return;
    }
    wrapped.textContent = text;
    const baseAttr = `data-vitals-cc-${shopCurrency.toLowerCase()}`;
    Array.from(wrapped.attributes).forEach((attr) => {
      if (/^data-vitals-cc-[a-z]{3}$/.test(attr.name) && attr.name !== baseAttr) wrapped.removeAttribute(attr.name);
    });
    wrapped.setAttribute(baseAttr, text);
  }
  /** The shop's own currency code (AutovonaConfig is written by snippets/autovona-head.liquid). */
  const shopCurrency = window.AutovonaConfig?.currency || 'AUD';

  /* ------------------------------------------------------------------ */
  /* Main product                                                        */
  /* ------------------------------------------------------------------ */
  function initProduct(root) {
    if (root.dataset.avReady) return;
    root.dataset.avReady = '1';
    const json = $('[data-av-product-json]', root);
    if (!json) return;
    const data = JSON.parse(json.textContent);
    const money = (c) => formatMoney(c, data.moneyFormat);
    const variantById = new Map(data.variants.map((v) => [v.id, v]));

    /* ---------------- Gallery ---------------- */
    const main = $('[data-av-gallery-main]', root);
    const zoom = $('[data-av-zoom]', root);
    const thumbs = $$('[data-av-thumb]', root);
    const counter = $('[data-av-gallery-index]', root);
    let active = 0;

    function setActive(index) {
      const thumb = thumbs[index];
      if (!thumb || !main) return;
      active = index;
      main.src = thumb.dataset.src;
      main.srcset = thumb.dataset.srcset;
      thumbs.forEach((t, i) => {
        setState(t, i === index);
        t.setAttribute('aria-current', String(i === index));
      });
      if (counter) counter.textContent = String(index + 1);
      // Keep the active thumbnail in view in the sideways-scrolling strip (without moving the page).
      const strip = thumb.parentElement;
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const s = strip.getBoundingClientRect();
        const t = thumb.getBoundingClientRect();
        strip.scrollBy({ left: t.left - s.left - (s.width - t.width) / 2, behavior: 'smooth' });
      }
    }

    thumbs.forEach((t, i) => t.addEventListener('click', () => setActive(i)));

    // Arrows over the main image: loop round at either end.
    const step = (dir) => {
      if (thumbs.length < 2) return;
      setActive((active + dir + thumbs.length) % thumbs.length);
    };
    $('[data-av-gallery-prev]', root)?.addEventListener('click', () => step(-1));
    $('[data-av-gallery-next]', root)?.addEventListener('click', () => step(1));

    if (zoom && main) {
      zoom.addEventListener('mouseenter', () => {
        main.style.transform = 'scale(1.9)';
      });
      zoom.addEventListener('mouseleave', () => {
        main.style.transform = 'scale(1)';
      });
      zoom.addEventListener('mousemove', (e) => {
        const r = zoom.getBoundingClientRect();
        main.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      });
    }

    const swipe = $('[data-av-swipe]', root);
    let touchX = null;
    swipe?.addEventListener('touchstart', (e) => {
      touchX = e.touches[0].clientX;
    }, { passive: true });
    swipe?.addEventListener('touchend', (e) => {
      if (touchX === null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > 40) {
        if (dx < 0) setActive(Math.min(thumbs.length - 1, active + 1));
        else setActive(Math.max(0, active - 1));
      }
      touchX = null;
    });

    /* ---------------- Sticky bar ---------------- */
    const sticky = $('[data-av-sticky]', root);
    let selectedVariant = variantById.get(data.firstVariant);
    // One item: the note is the shipping promise (Theme settings → Offer defaults, set in Liquid).
    const stickyPromise = $('[data-av-sticky-note]', root)?.dataset.default || '';

    /**
     * What the buy box would add right now: items, total after the bundle discount, the
     * struck-through total and a short note. The bundle selector below replaces this with the
     * selected tier + ticked add-on, so the sticky bar always adds exactly what the buy box shows.
     */
    let currentSelection = () => ({
      items: selectedVariant ? [{ id: selectedVariant.id, quantity: 1 }] : [],
      total: selectedVariant?.price || 0,
      compare: selectedVariant?.compare || 0,
      note: stickyPromise,
    });

    function updateSticky() {
      if (!sticky) return;
      const sel = currentSelection();
      setMoney($('[data-av-sticky-price]', sticky), money(sel.total));
      const compareEl = $('[data-av-sticky-compare]', sticky);
      if (compareEl) {
        const show = sel.compare > sel.total;
        compareEl.classList.toggle('av-hidden', !show);
        if (show) setMoney(compareEl, money(sel.compare));
      }
      const note = $('[data-av-sticky-note]', sticky);
      if (note) note.textContent = sel.note;
      const img = $('[data-av-sticky-img]', sticky);
      if (img && selectedVariant?.image) img.src = selectedVariant.image;
    }

    if (sticky) {
      // Show the bar once the main Add to Cart button has scrolled out of view above the
      // screen, so two "Add to Cart" buttons are never visible at the same time. (It watches
      // Add to Cart itself: the Buy It Now button is hidden while the express buttons show,
      // and a hidden element never "scrolls past", which kept the bar from ever appearing.)
      // Browsers without IntersectionObserver fall back to a scroll distance.
      const anchor = $('[data-av-bundle-add]', root);
      if (anchor && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          setState(sticky, !entry.isIntersecting && entry.boundingClientRect.top < 0);
        }).observe(anchor);
      } else {
        const threshold = Number(sticky.dataset.threshold) || 620;
        const onScroll = () => setState(sticky, window.scrollY > threshold);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
      $('[data-av-sticky-add]', sticky)?.addEventListener('click', () => {
        const { items } = currentSelection();
        if (items.length) cart()?.add(items).catch(() => {});
      });
    }

    /** Price under the title: the picked variant's price, compare-at price and saving. */
    const mainPrice = $('[data-av-main-price-wrap]', root);
    function updateMainPrice(v) {
      if (!mainPrice || !v) return;
      setMoney($('[data-av-main-price]', mainPrice), money(v.price));
      const save = v.compare > v.price ? v.compare - v.price : 0;
      const was = $('[data-av-main-compare]', mainPrice);
      const saveEl = $('[data-av-main-save]', mainPrice);
      was?.classList.toggle('av-hidden', !save);
      saveEl?.classList.toggle('av-hidden', !save);
      if (save) {
        setMoney(was, money(v.compare));
        setMoney($('[data-av-main-save-amount]', mainPrice), money(save));
      }
    }

    /** Variant preview: swap the gallery image, the price and the sticky bar's variant. */
    function previewVariant(id) {
      const v = variantById.get(id);
      if (!v) return;
      selectedVariant = v;
      if (v.media >= 0) setActive(v.media);
      updateMainPrice(v);
      updateSticky();
    }

    /* ---------------- Bundle selector ---------------- */
    const bundle = $('[data-av-bundle]', root);
    if (bundle) {
      const tiers = $$('[data-av-tier]', bundle).map((btn) => ({
        btn,
        wrap: btn.closest('[data-av-tier-wrap]'),
        qty: Number(btn.dataset.qty),
      }));
      let tierIndex = Number(data.defaultTier) || 0;
      const picks = [data.firstVariant, data.firstVariant, data.firstVariant];
      const addBtn = $('[data-av-bundle-add]', bundle);
      const buyBtn = $('[data-av-bundle-buy]', bundle);
      const addon = $('[data-av-addon]', bundle);
      const addonCheck = addon && $('[data-av-addon-check]', addon);
      const express = $('[data-av-express]', bundle);
      const expressId = express && $('[data-av-express-id]', express);
      const expressQty = express && $('[data-av-express-qty]', express);

      // Two bundle discounts can apply: this product's own (its metafields, 0 = none — only its own
      // units count) and the store-wide one (every item in the order counts). They don't combine:
      // Shopify applies whichever saves more, so every price here is worked out the same way.
      const rule = (name) => ({ 2: Number(bundle.getAttribute(`data-${name}-2`)) || 0, 3: Number(bundle.getAttribute(`data-${name}-3`)) || 0 });
      const own = rule('own');
      const gen = rule('gen');
      const step = (r, n) => (n >= 3 ? r[3] : n >= 2 ? r[2] : 0);
      // Shopify takes the discount off in whole cents, rounded down — mirror it so the price
      // shown here equals the cart / checkout price to the cent (integer maths: no float drift).
      const cut = (gross, pct) => Math.floor((gross * pct) / 100);

      /**
       * The discount the cart applies to `units` of this product (worth `productGross`) plus
       * `extraQty` other items worth `extraGross`: { amount, pct, store } — store = the store-wide one won.
       */
      function bestDiscount(productGross, units, extraGross = 0, extraQty = 0) {
        const ownPct = step(own, units);
        const genPct = step(gen, units + extraQty);
        const ownCut = cut(productGross, ownPct);
        const genCut = cut(productGross + extraGross, genPct);
        return genCut > ownCut ? { amount: genCut, pct: genPct, store: true } : { amount: ownCut, pct: ownPct, store: false };
      }

      const tierGross = (t) => {
        let price = 0;
        let compare = 0;
        for (let u = 0; u < t.qty; u++) {
          const v = variantById.get(picks[u]) || variantById.get(data.firstVariant);
          price += v.price;
          compare += v.compare || v.price;
        }
        return { price, compare };
      };

      const tierTotal = (t) => {
        const { price, compare } = tierGross(t);
        const total = price - bestDiscount(price, t.qty).amount;
        return { total, perItem: Math.round(total / t.qty), compare };
      };

      const addonOn = () => Boolean(addonCheck?.checked);

      function render() {
        tiers.forEach((t, i) => {
          const selected = i === tierIndex;
          setState(t.btn, selected);
          setState($('[data-av-tier-radio]', t.btn), selected);
          $('[data-av-tier-dot]', t.btn)?.classList.toggle('av-hidden', !selected);
          const totalEl = $('[data-av-tier-total]', t.btn);
          setState(totalEl, selected);
          const { total, perItem, compare } = tierTotal(t);
          setMoney(totalEl, money(total));
          setMoney($('[data-av-tier-per]', t.btn), money(perItem));
          setMoney($('[data-av-tier-compare]', t.btn), money(compare));
          $('[data-av-tier-picks]', t.wrap)?.classList.toggle('av-hidden', !selected);
        });
        $$('[data-av-pick]', bundle).forEach((b) => setState(b, Number(b.dataset.variantId) === picks[Number(b.dataset.avPick)]));
        $$('[data-av-pick-select]', bundle).forEach((sel) => {
          const want = String(picks[Number(sel.dataset.avPickSelect)]);
          if (sel.value !== want) sel.value = want;
        });

        const sel = selection();
        if (addBtn && !addBtn.disabled) setMoney($('[data-av-bundle-price]', addBtn), money(sel.total));
        renderAddonNote();
        renderGuarantee(tiers[tierIndex].qty);
        syncExpress();
        updateSticky();
      }

      /**
       * Guarantee row: a product can give 2 / 3+ items a longer guarantee. The text for each quantity
       * is rendered into data-name-N / data-body-N (snippets/av-offer-guarantee); show the selected one.
       */
      const guarantee = $('[data-av-guarantee]', root);
      function renderGuarantee(qty) {
        if (!guarantee) return;
        const n = Math.min(Math.max(qty, 1), 3);
        const name = guarantee.getAttribute(`data-name-${n}`);
        const body = guarantee.getAttribute(`data-body-${n}`);
        const nameEl = $('[data-av-guarantee-name]', guarantee);
        const bodyEl = $('[data-av-guarantee-body]', guarantee);
        if (nameEl && name) nameEl.textContent = name;
        if (bodyEl && body) bodyEl.textContent = body;
      }

      /**
       * Add-on note: when ticking it lets the store-wide bundle beat what the shopper gets now, say so
       * with the real numbers — [amount] is what the whole order then saves (rounded down to the
       * cent, like Shopify). When this product's own bundle still saves more, the plain note shows.
       */
      function renderAddonNote() {
        const note = addon && $('[data-av-addon-note]', addon);
        if (!note) return;
        const t = tiers[tierIndex];
        const price = tierGross(t).price;
        const before = bestDiscount(price, t.qty);
        const after = bestDiscount(price, t.qty, Number(addon.dataset.price) || 0, 1);
        let text = addon.dataset.noteBase || '';
        if (after.store && after.pct > before.pct) {
          text = ((t.qty + 1 >= 3 ? addon.dataset.noteBoost : addon.dataset.noteUnlock) || text).replace('[amount]', money(after.amount));
        }
        note.textContent = text;
      }

      /**
       * Express buttons buy a single variant, so they are only offered when the selection is one
       * variant (in any quantity) and the add-on is not ticked; otherwise "Buy It Now" is shown.
       */
      function syncExpress() {
        if (!express) return;
        const items = bundleItems();
        const single = items.length === 1 && !addonOn();
        express.classList.toggle('av-hidden', !single);
        buyBtn?.classList.toggle('av-hidden', single);
        if (!single || !expressId || !expressQty) return;
        const changed = String(items[0].id) !== expressId.value || String(items[0].quantity) !== expressQty.value;
        expressId.value = String(items[0].id);
        expressQty.value = String(items[0].quantity);
        if (changed) expressId.dispatchEvent(new Event('change', { bubbles: true }));
      }

      tiers.forEach((t, i) =>
        t.btn.addEventListener('click', () => {
          tierIndex = i;
          render();
        })
      );

      bundle.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-av-pick]');
        if (pick && !pick.disabled) {
          const id = Number(pick.dataset.variantId);
          picks[Number(pick.dataset.avPick)] = id;
          render();
          previewVariant(id);
        }
      });

      // Dropdown picker (products with several options or many variants).
      bundle.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-av-pick-select]');
        if (!sel) return;
        const id = Number(sel.value);
        picks[Number(sel.dataset.avPickSelect)] = id;
        render();
        previewVariant(id);
      });

      const bundleItems = () => {
        const t = tiers[tierIndex];
        if (!data.hasVariants) return [{ id: data.firstVariant, quantity: t.qty }];
        const counts = new Map();
        for (let u = 0; u < t.qty; u++) counts.set(picks[u], (counts.get(picks[u]) || 0) + 1);
        return [...counts].map(([id, quantity]) => ({ id, quantity }));
      };

      /** The selected tier plus the add-on when ticked, priced the way the cart will price it. */
      function selection() {
        const t = tiers[tierIndex];
        const items = bundleItems();
        const { price, compare: tierCompare } = tierGross(t);
        let compare = tierCompare;
        let extra = 0;
        if (addonOn()) {
          extra = Number(addon.dataset.price) || 0;
          compare += extra;
          items.push({ id: Number(addon.dataset.variantId), quantity: 1 });
        }
        const best = bestDiscount(price, t.qty, extra, addonOn() ? 1 : 0);
        const pct = best.amount > 0 ? best.pct : 0;
        const note =
          t.qty === 1 && !addonOn()
            ? stickyPromise
            : `${t.qty} ${t.qty === 1 ? 'item' : 'items'}${addonOn() ? ' + add-on' : ''}${pct > 0 ? ` · save ${pct}%` : ''}`;
        return { items, total: price + extra - best.amount, compare, note };
      }
      currentSelection = selection;

      addonCheck?.addEventListener('change', render);
      // The button also submits a plain form (for taps before this script loads) — stop that here.
      addBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        cart()?.add(selection().items).catch(() => {});
      });
      buyBtn?.addEventListener('click', async () => {
        try {
          // "Buy It Now" goes straight to checkout — no stop at the cart page.
          await cart()?.add(selection().items, { open: false });
          window.location.href = window.AutovonaConfig?.routes?.checkout || '/checkout';
        } catch (error) {
          /* toast already shown */
        }
      });

      render();
    }

    /* Delivery timeline: see initTimeline() below (runs on its own). */

    /* ---------------- Launch bonus: gone once its end date has passed ---------------- */
    // Liquid already leaves it out after the end date (shop's date); this covers a cached page.
    const launch = $('[data-av-launch]', root);
    if (launch?.dataset.ends) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      if (today > launch.dataset.ends) launch.classList.add('av-hidden');
    }

    /* ---------------- Sale countdown (to end of today) ---------------- */
    const countdown = $('[data-av-countdown]', root);
    if (countdown) {
      const time = $('[data-av-countdown-time]', countdown);
      const dateEl = $('[data-av-countdown-date]', countdown);
      const dispatch = countdown.dataset.mode === 'dispatch';
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const now = new Date();
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        const total = Math.floor(Math.max(0, end - now) / 1000);
        if (dispatch) {
          // "9h 12m" reads better inside a sentence than a ticking clock.
          const h = Math.floor(total / 3600);
          const m = Math.floor((total % 3600) / 60);
          time.textContent = h > 0 ? `${h}h ${pad(m)}m` : `${Math.max(m, 1)} min`;
          if (dateEl) dateEl.textContent = dispatchDate(Number(countdown.dataset.shipMax) || 2);
        } else {
          const parts = [pad(Math.floor(total / 3600)), ':', pad(Math.floor((total % 3600) / 60)), ':', pad(total % 60)];
          const nodes = [...time.childNodes].filter((n) => n.nodeType === 3);
          if (nodes.length === 5) nodes.forEach((n, i) => (n.nodeValue = parts[i]));
          else time.textContent = parts.join('');
        }
        countdown.classList.remove('av-hidden');
      };
      tick();
      window.setInterval(tick, dispatch ? 15000 : 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') tick();
      });
    }

    /* ---------------- Size chart ---------------- */
    const chart = $('[data-av-sizechart]', root);
    if (chart) {
      const open = () => chart.classList.remove('av-hidden');
      const close = () => chart.classList.add('av-hidden');
      root.addEventListener('click', (e) => {
        if (e.target.closest('[data-av-sizechart-open]')) open();
        else if (e.target.closest('[data-av-sizechart-close]')) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Description / Specifications tabs (ProductTabs.tsx)                 */
  /* ------------------------------------------------------------------ */
  function initTabs(root) {
    if (root.dataset.avReady) return;
    root.dataset.avReady = '1';
    const tabs = $$('[data-av-tab]', root);
    tabs.forEach((tab) =>
      tab.addEventListener('click', () => {
        tabs.forEach((t) => setState(t, t === tab));
        $$('[data-av-panel]', root).forEach((p) => p.classList.toggle('av-hidden', p.dataset.avPanel !== tab.dataset.avTab));
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /* Frequently Bought Together                                          */
  /* ------------------------------------------------------------------ */
  function initFbt(root) {
    if (root.dataset.avReady) return;
    root.dataset.avReady = '1';
    const format = root.dataset.moneyFormat;
    const boxes = $$('input[data-av-fbt-item]', root);
    const totalEl = $('[data-av-fbt-total]', root);
    const grossEl = $('[data-av-fbt-gross]', root);
    const dealEl = $('[data-av-fbt-deal]', root);
    const countLabel = $('[data-av-fbt-count]', root);
    const button = $('[data-av-fbt-add]', root);
    // "2,10,3,15" = 2+ items → 10 % off, 3+ items → 15 % off (the cart's automatic discounts).
    const [q2, p2, q3, p3] = (root.dataset.rules || '').split(',').map(Number);
    const pctFor = (n) => (q3 && n >= q3 ? p3 : q2 && n >= q2 ? p2 : 0);

    function update() {
      const chosen = boxes.filter((b) => b.checked);
      const gross = chosen.reduce((sum, b) => sum + Number(b.dataset.price), 0);
      const count = chosen.length;
      const pct = pctFor(count) || 0;
      const total = gross - Math.floor((gross * pct) / 100);
      if (totalEl) setMoney(totalEl, formatMoney(total, format));
      if (grossEl) {
        grossEl.classList.toggle('av-hidden', !pct);
        if (pct) setMoney(grossEl, formatMoney(gross, format));
      }
      if (dealEl) {
        dealEl.classList.toggle('av-hidden', !pct);
        const text = $('[data-av-fbt-deal-text]', dealEl);
        if (text && pct) text.textContent = `${pct}% bundle discount included`;
      }
      if (countLabel) {
        const nodes = [...countLabel.childNodes].filter((n) => n.nodeType === 3);
        // "Total for " · n · " " · item(s)
        if (nodes.length >= 4) {
          nodes[1].nodeValue = String(count);
          nodes[3].nodeValue = count === 1 ? 'item' : 'items';
        }
      }
      if (button) {
        const nodes = [...button.childNodes].filter((n) => n.nodeType === 3);
        // "Add " · n · " to Cart"
        if (nodes.length >= 3) nodes[1].nodeValue = String(count);
        button.disabled = count === 0;
      }
    }

    boxes.forEach((b) => b.addEventListener('change', update));
    button?.addEventListener('click', () => {
      const items = boxes.filter((b) => b.checked).map((b) => ({ id: Number(b.dataset.variantId), quantity: 1 }));
      if (items.length) cart()?.add(items).catch(() => {});
    });
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Watch & Shop — short clips; tap one to watch it full screen and add  */
  /* the product from the player.                                        */
  /* ------------------------------------------------------------------ */
  function initShopVideos(root) {
    if (root.dataset.avReady) return;
    root.dataset.avReady = '1';
    const cards = $$('[data-av-sv-card]', root);
    const modal = $('[data-av-sv-modal]', root);
    const player = modal && $('[data-av-sv-player]', modal);
    const photo = modal && $('[data-av-sv-image]', modal);
    if (!cards.length || !modal || !player) return;

    // Muted previews play only while on screen (and never for people who prefer less motion).
    // Tiles marked data-av-sv-still (the moving customer strip) keep their poster until tapped.
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const previews = cards.filter((card) => !card.hasAttribute('data-av-sv-still') && $('video', card));
    if (!still && previews.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            const video = $('video', entry.target);
            if (!video) return;
            if (entry.isIntersecting) video.play().catch(() => {});
            else video.pause();
          }),
        { threshold: 0.6 }
      );
      previews.forEach((card) => io.observe(card));
    }

    // Every real tile, in page order (the looping strip's duplicate copies are skipped), so the
    // viewer can step through them with its arrows, a swipe or the ← → keys.
    const navCards = cards.filter((card) => card.getAttribute('aria-hidden') !== 'true');
    const counter = $('[data-av-sv-count]', modal);
    let current = -1;
    let lastCard = null;
    function open(card) {
      const wasOpen = !modal.classList.contains('av-hidden');
      player.pause();
      current = navCards.indexOf(card);
      if (counter) counter.textContent = current >= 0 ? `${current + 1} / ${navCards.length}` : '';
      if (!card.classList.contains('av-hidden')) lastCard = card;
      const isPhoto = card.dataset.type === 'image';
      player.hidden = isPhoto;
      if (photo) {
        photo.hidden = !isPhoto;
        photo.src = isPhoto ? card.dataset.full || '' : '';
        photo.alt = isPhoto ? card.dataset.caption || card.dataset.title || '' : '';
      }
      if (!isPhoto) {
        const preview = $('video', card);
        player.replaceChildren(...$$('source', preview).map((s) => s.cloneNode()));
        player.poster = preview?.poster || '';
        player.load();
      }
      $('[data-av-sv-caption]', modal).textContent = card.dataset.caption || '';
      $('[data-av-sv-title]', modal).textContent = card.dataset.title || '';
      $('[data-av-sv-price]', modal).textContent = card.dataset.price || '';
      const add = $('[data-av-sv-add]', modal);
      add.dataset.variantId = card.dataset.variantId || '';
      add.disabled = card.dataset.available !== 'true';
      const link = $('[data-av-sv-link]', modal);
      if (link) link.href = card.dataset.url || '#';
      modal.classList.remove('av-hidden');
      document.body.classList.add('av-no-scroll');
      if (!isPhoto) {
        player.muted = false;
        player.play().catch(() => {});
      }
      if (!wasOpen) $('[data-av-sv-close]', modal)?.focus({ preventScroll: true });
    }

    function go(dir) {
      if (navCards.length < 2 || current < 0) return;
      open(navCards[(current + dir + navCards.length) % navCards.length]);
    }

    function close() {
      if (modal.classList.contains('av-hidden')) return;
      player.pause();
      modal.classList.add('av-hidden');
      // Nothing else can be open underneath the full-screen player (the cart opens after it closes).
      document.body.classList.remove('av-no-scroll');
      lastCard?.focus({ preventScroll: true });
    }

    cards.forEach((card) => card.addEventListener('click', () => open(card)));
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-av-sv-close]')) close();
      // The add button is handled by the global [data-av-add] handler (adds + opens the cart);
      // close the player first so the cart drawer is not hidden behind it.
      else if (e.target.closest('[data-av-sv-add]')) close();
      else if (e.target.closest('[data-av-sv-prev]')) go(-1);
      else if (e.target.closest('[data-av-sv-next]')) go(1);
    });
    // "See all photos & videos" opens the viewer at the given tile.
    $$('[data-av-sv-open]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        const card = navCards[Number(btn.dataset.avSvOpen) || 0];
        if (card) open(card);
      })
    );
    // Swipe sideways on the photo / video to move between them.
    const panel = $('.av-x-sv-modal__panel', modal);
    let swipeX = null;
    let swipeY = null;
    panel?.addEventListener('touchstart', (e) => {
      swipeX = e.touches[0].clientX;
      swipeY = e.touches[0].clientY;
    }, { passive: true });
    panel?.addEventListener('touchend', (e) => {
      if (swipeX === null) return;
      const dx = e.changedTouches[0].clientX - swipeX;
      const dy = e.changedTouches[0].clientY - swipeY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && !e.target.closest('.av-x-sv-modal__bar')) go(dx < 0 ? 1 : -1);
      swipeX = null;
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (modal.classList.contains('av-hidden')) return;
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Delivery timeline (Ordered → Shipped → Delivered) — real dates from  */
  /* the shopper's own clock. Page HTML can be served from cache long     */
  /* after it was made, so the dates are always recalculated here: on     */
  /* load, whenever the page is shown again (tab switch, back button,     */
  /* waking the phone) and at midnight. Runs on its own, so nothing else  */
  /* on the page can stop the dates from updating.                        */
  /* ------------------------------------------------------------------ */
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /** Latest dispatch date for an order placed today: `n` working days from today, e.g. "Wed 8 Oct". */
  function dispatchDate(n) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    let left = n;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
    }
    return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  }

  function initTimeline(timeline) {
    if (timeline.dataset.avReady) return;
    timeline.dataset.avReady = '1';
    const days = (key) => Number(timeline.dataset[key]) || 0;
    const fmt = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
    const today = () => {
      const d = new Date();
      d.setHours(12, 0, 0, 0); // midday: immune to daylight-saving changes
      return d;
    };
    const addDays = (n) => {
      const d = today();
      d.setDate(d.getDate() + n);
      return d;
    };
    // Dispatch counts working days only (no shipping at weekends).
    const addBusinessDays = (n) => {
      const d = today();
      let left = n;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
      }
      return d;
    };
    const range = (a, b) => (fmt(a) === fmt(b) ? fmt(a) : `${fmt(a)} – ${fmt(b)}`);
    const shipped = $('[data-av-tl="shipped"]', timeline);
    const delivered = $('[data-av-tl="delivered"]', timeline);

    let shownFor = '';
    let midnight = null;
    const render = () => {
      const key = new Date().toDateString();
      if (key !== shownFor) {
        shownFor = key;
        if (shipped) shipped.textContent = range(addBusinessDays(days('shipMin')), addBusinessDays(days('shipMax')));
        if (delivered) delivered.textContent = range(addDays(days('deliverMin')), addDays(days('deliverMax')));
      }
      clearTimeout(midnight);
      const next = new Date();
      next.setHours(24, 0, 5, 0);
      midnight = setTimeout(render, next - Date.now());
    };
    render();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') render();
    });
    window.addEventListener('pageshow', render);
    window.addEventListener('focus', render);
  }

  /* Demonstration videos (Benefits): play muted while on screen, pause when scrolled away. */
  function initInview(scope) {
    const videos = $$('[data-av-inview] video', scope).filter((v) => !v.dataset.avInview);
    if (!videos.length) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    videos.forEach((v) => {
      v.dataset.avInview = '1';
      v.muted = true;
      if (still) v.controls = true;
    });
    if (still || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.play().catch(() => {});
          else entry.target.pause();
        }),
      { threshold: 0.5 }
    );
    videos.forEach((v) => io.observe(v));
  }

  function init(scope = document) {
    $$('[data-av-product]', scope).forEach(initProduct);
    $$('[data-av-tabs]', scope).forEach(initTabs);
    $$('[data-av-fbt]', scope).forEach(initFbt);
    $$('[data-av-shop-videos]', scope).forEach(initShopVideos);
    $$('[data-av-timeline]', scope).forEach(initTimeline);
    initInview(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
